import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { http, formatApiError } from "./api";
import VidhyaWordmark from "./components/VidhyaWordmark";
import { COUNTRIES } from "./data/countries";

// Allowed next-step degrees per standardized academic stage (mirrors backend guardrail).
export const degreesForStage = (stage) => {
  const s = (stage || "").toLowerCase();
  if (s.includes("high school")) return ["Bachelors"];
  if (s.includes("undergrad")) return ["Bachelors", "Masters"]; // checked before "graduate"
  if (s.includes("graduate")) return ["Masters", "PhD"];
  return ["Bachelors", "Masters", "PhD"];
};

const STEPS = [
  {
    key: "academic_stage",
    label: "Where are you in your education right now?",
    sub: "We use a global standard so deadlines and requirements line up no matter where you study.",
    grouped: [
      { label: "High School", options: ["High School - Junior", "High School - Senior"] },
      { label: "Undergraduate", options: ["Undergraduate - 1st Year", "Undergraduate - 2nd Year", "Undergraduate - 3rd Year", "Undergraduate - 4th Year"] },
      { label: "Graduate", options: ["Graduate - 1st Year", "Graduate - 2nd Year"] },
    ],
    multi: false,
  },
  {
    key: "intended_degree_level",
    label: "What degree level are you applying for?",
    sub: "We only show the levels that make sense for your current stage.",
    dynamic: (answers) => degreesForStage(answers.academic_stage),
    multi: false,
  },
  {
    key: "intended_major",
    label: "What do you want to study?",
    sub: "A rough direction is fine — it drives every match. You can change it anytime.",
    free: true,
    placeholder: "e.g. Computer Science, Nursing, Business, Psychology…",
  },
  {
    key: "last_academic_subject_taken",
    label: "What's the last major subject you studied?",
    sub: "This sharpens how accurately we match programs and recommend next steps.",
    free: true,
    placeholder: "e.g. AP Calculus, A-Level Physics, Final-year Thesis…",
  },
  {
    key: "stats",
    label: "Your academic stats",
    sub: "Approximate is fine — this is what powers honest fit scores. Add what you have.",
    type: "stats",
  },
  {
    key: "country_of_citizenship",
    label: "What's your country of citizenship?",
    sub: "This drives international vs. domestic fees, visa needs, and scholarship eligibility.",
    type: "country",
    placeholder: "Start typing your country…",
  },
  {
    key: "target_countries",
    label: "Where do you want to study?",
    sub: "Pick one or more destination countries — this drives which programs, deadlines, and fees we research.",
    options: [
      "United States",
      "United Kingdom",
      "Canada",
      "Australia",
      "Germany",
      "Netherlands",
      "Ireland",
      "Singapore",
      "France",
      "New Zealand",
    ],
    multi: true,
  },
  {
    key: "intake_term",
    label: "When do you want to start?",
    sub: "This sets your deadlines, funding window, and which application cycle you're in.",
    options: ["Fall 2026", "Spring 2027", "Fall 2027", "Not sure yet"],
    multi: false,
  },
  {
    key: "preferences",
    label: "What matters most to you in a school?",
    sub: "Pick a few — Vidhya weighs these when matching colleges and programs.",
    options: [
      "Top academics & prestige",
      "Affordability & financial aid",
      "Location & city life",
      "Strong career outcomes",
      "Research opportunities",
      "Campus community & clubs",
    ],
    multi: true,
  },
  {
    key: "budget",
    label: "What's your budget?",
    sub: "We'll prioritize schools and scholarships that actually fit.",
    options: [
      "Need a full ride / maximum aid",
      "Up to $20,000 / year",
      "$20,000 – $40,000 / year",
      "$40,000 – $70,000 / year",
      "Budget is not a constraint",
    ],
    multi: false,
  },
];

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({
    academic_stage: "",
    intended_degree_level: "",
    intended_major: "",
    last_academic_subject_taken: "",
    gpa: "",
    sat_score: "",
    act_score: "",
    country_of_citizenship: "",
    target_countries: [],
    intake_term: "",
    preferences: [],
    budget: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // Mirror of backend's looks_like_garbage so the user gets instant feedback.
  const looksLikeGarbage = (text) => {
    const s = (text || "").trim();
    if (s.length < 2) return true;
    if (/(qwert|wert|erty|asdf|sdfg|dfgh|zxcv|xcvb|cvbn|hjkl|jklm)/i.test(s)) return true;
    const hasSpace = /\s/.test(s);
    if (!hasSpace && s.length >= 5) {
      const letters = s.toLowerCase().replace(/[^a-z]/g, "");
      if (letters.length >= 5 && (letters.match(/[aeiouy]/g) || []).length === 0) return true;
    }
    return false;
  };

  const step = STEPS[stepIdx];
  const value = answers[step.key];
  const dynamicOpts = step.dynamic ? step.dynamic(answers) : step.options;
  const canNext = step.type === "stats"
    ? !!(answers.gpa || answers.sat_score || answers.act_score)
    : step.multi
      ? Array.isArray(value) && value.length > 0
      : (value || "").toString().trim().length > 0;

  const pick = (opt) => {
    if (step.multi) {
      const arr = Array.isArray(value) ? [...value] : [];
      setAnswers({ ...answers, [step.key]: arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt] });
    } else if (step.key === "academic_stage") {
      // Changing stage may invalidate a previously picked degree — reset it.
      const stillValid = degreesForStage(opt).includes(answers.intended_degree_level);
      setAnswers({ ...answers, academic_stage: opt, intended_degree_level: stillValid ? answers.intended_degree_level : "" });
    } else {
      setAnswers({ ...answers, [step.key]: opt });
    }
  };

  const next = async () => {
    setErr("");
    if (step.free && looksLikeGarbage(value)) {
      setErr("That doesn't look like a real answer. Try a real value (e.g. ‘Computer Science’).");
      return;
    }
    if (step.type === "country" && !COUNTRIES.includes((value || "").trim())) {
      setErr("Please pick a country from the list.");
      return;
    }
    if (stepIdx < STEPS.length - 1) {
      setStepIdx(stepIdx + 1);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await http.post("/onboarding/complete", {
        academic_stage: answers.academic_stage,
        intended_degree_level: answers.intended_degree_level,
        intended_major: answers.intended_major,
        last_academic_subject_taken: answers.last_academic_subject_taken,
        gpa: answers.gpa,
        sat_score: answers.sat_score,
        act_score: answers.act_score,
        intake_term: answers.intake_term,
        preferences: answers.preferences,
        budget: answers.budget,
        country_of_citizenship: answers.country_of_citizenship,
        citizenship: answers.country_of_citizenship,
        student_country: answers.country_of_citizenship,
        target_countries: answers.target_countries,
      });
      if (user && setUser) setUser({ ...user, onboarded: true });
      // Drop the focus uni id so the app opens straight on the fit report — the "aha".
      if (data && data.focus_university_id) {
        sessionStorage.setItem("vidhya_onboard_focus", data.focus_university_id);
      }
      nav("/app/dashboard", { replace: true });
      setTimeout(() => window.location.reload(), 50);
      return data;
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const back = () => { if (stepIdx > 0) setStepIdx(stepIdx - 1); };

  const statField = (k, ph, autofocus) => (
    <input
      className="onb-input"
      type="text"
      value={answers[k] || ""}
      placeholder={ph}
      onChange={(e) => setAnswers({ ...answers, [k]: e.target.value })}
      autoFocus={autofocus}
      data-testid={`onboarding-${k.replace(/_/g, "-")}-input`}
    />
  );

  const renderOption = (opt) => {
    const selected = step.multi ? (value || []).includes(opt) : value === opt;
    return (
      <button
        key={opt}
        type="button"
        className={`onb-option ${selected ? "selected" : ""}`}
        onClick={() => pick(opt)}
        data-testid={`onb-opt-${opt.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
      >
        <span>{opt}</span>
        {selected && (
          <span className="onb-option-check">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2.5 7.5L5.5 10.5L11.5 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="onb-shell" data-testid="onboarding-page">
      <div className="onb-rail">
        <div className="onb-brand">
          <VidhyaWordmark size={28} tone="lime" />
        </div>
        <div className="onb-rail-eyebrow">Planning mode · One-time setup</div>
        <h1 className="onb-rail-h">Let's build your<br /><em>shortlist</em>.</h1>
        <p className="onb-rail-sub">
          A few quick questions about you and your goals. We'll then research live and seed your workspace with a
          personalized mix of reach, target, and safety schools — then drop you straight into your first fit report.
        </p>
        <div className="onb-step-list">
          {STEPS.map((s, i) => (
            <div key={s.key} className={`onb-step-item ${i === stepIdx ? "active" : ""} ${i < stepIdx ? "done" : ""}`}>
              <span className="onb-step-num">{String(i + 1).padStart(2, "0")}</span>
              <span className="onb-step-title">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="onb-main">
        <div className="onb-progress">
          <div className="onb-progress-bar" style={{ width: `${((stepIdx + 1) / STEPS.length) * 100}%` }}></div>
        </div>

        {submitting ? (
          <div className="onb-loading" data-testid="onboarding-loading">
            <div className="onb-loading-mark"><div className="onb-loading-pulse"></div></div>
            <h2 className="onb-loading-h">Building your shortlist…</h2>
            <p className="onb-loading-sub">
              Matching colleges to your stats, intake term and priorities — then pulling tuition, deadlines and aid.
              This takes ~30 seconds.
            </p>
            <div className="onb-loading-steps">
              <div>● Picking 4 best-fit colleges for your stats</div>
              <div>● Pulling tuition, deadlines &amp; aid</div>
              <div>● Preparing your first fit report</div>
            </div>
          </div>
        ) : (
          <div className="onb-step" data-testid={`onboarding-step-${stepIdx}`}>
            <div className="onb-step-meta">Step {stepIdx + 1} of {STEPS.length}</div>
            <h2 className="onb-question">{step.label}</h2>
            <p className="onb-question-sub">{step.sub}</p>

            {step.type === "stats" ? (
              <div className="onb-stats" data-testid="onboarding-stats">
                <div style={{ marginBottom: 14 }}>
                  <label className="onb-field-label">GPA / grade average <span>(any scale)</span></label>
                  {statField("gpa", "e.g. 3.8 / 85% / AAB", true)}
                </div>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <label className="onb-field-label">SAT total <span>(optional)</span></label>
                    {statField("sat_score", "e.g. 1450")}
                  </div>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <label className="onb-field-label">ACT composite <span>(optional)</span></label>
                    {statField("act_score", "e.g. 32")}
                  </div>
                </div>
                <p className="onb-stats-hint">Haven't tested yet? Just add your GPA — you can fill scores in later.</p>
              </div>
            ) : step.type === "country" ? (
              <div data-testid="onboarding-country">
                <input
                  className="onb-input"
                  type="text"
                  list="onb-countries"
                  value={value || ""}
                  placeholder={step.placeholder}
                  onChange={(e) => setAnswers({ ...answers, [step.key]: e.target.value })}
                  autoFocus
                  data-testid="onboarding-citizenship-input"
                />
                <datalist id="onb-countries">
                  {COUNTRIES.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
            ) : step.free ? (
              <input
                className="onb-input"
                type="text"
                value={value || ""}
                placeholder={step.placeholder}
                onChange={(e) => setAnswers({ ...answers, [step.key]: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter" && canNext) next(); }}
                autoFocus
                data-testid={`onboarding-${step.key.replace(/_/g, "-")}-input`}
              />
            ) : step.grouped ? (
              <div className="onb-grouped" data-testid="onboarding-grouped">
                {step.grouped.map((g) => (
                  <div key={g.label} className="onb-group">
                    <div className="onb-group-label">{g.label}</div>
                    <div className="onb-options">{g.options.map(renderOption)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="onb-options">
                {(dynamicOpts || []).map(renderOption)}
              </div>
            )}

            {err && <div className="auth-error" style={{ marginTop: 18 }} data-testid="onboarding-error">{err}</div>}

            <div className="onb-actions">
              {stepIdx > 0 && (
                <button type="button" className="onb-back-btn" onClick={back} data-testid="onb-back-btn">← Back</button>
              )}
              <button type="button" className="onb-next-btn" onClick={next} disabled={!canNext} data-testid="onb-next-btn">
                {stepIdx === STEPS.length - 1 ? "Build my shortlist" : "Continue"}
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
